"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { User } from "@supabase/supabase-js";
import { WorkDb } from "../app/lib/db/workDb";

export interface AuthenticationContextType {
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthenticationContext = createContext<AuthenticationContextType | undefined>(undefined);

interface AuthenticationProviderProps {
  children: React.ReactNode;
}

export const AuthenticationProvider: React.FC<AuthenticationProviderProps> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const supabase = useMemo(() => WorkDb.initialize(), []);

  const signOut = useCallback(async () => {
    try {
      // Use local scope to avoid the noisy 403 that can occur when the server no longer has the session.
      await supabase.auth.signOut({ scope: "local" });
    } catch (err: any) {
      // Ignore "Auth session missing" – it means we were already logged-out.
      if (err?.message?.toLowerCase().includes("session missing")) {
        console.debug("Already signed out on the server – proceeding.");
      } else {
        console.error("Error signing out:", err?.message || err);
        throw err; // bubble up other errors so callers can handle
      }
    }

    // Clear local state so the app UI updates immediately.
    setUser(null);
    setIsLoading(false);
  }, [supabase.auth, setUser, setIsLoading]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // console.log('Auth state changed:', _event, session?.user);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [supabase.auth]);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      signOut,
    }),
    [user, isLoading, signOut]
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthenticationContext);
  if (context === undefined) {
    // During server-side rendering, context might be undefined
    // Return a default state instead of throwing an error
    if (typeof window === 'undefined') {
      return {
        user: null,
        isLoading: true,
        signOut: async () => {},
      };
    }
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
